import { Component, inject, input, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { FeedbackService, FeedbackThread, FeedbackReply } from '../../services/feedback.service';
import { TranslationService } from '../../services/translation.service';

@Component({
  selector: 'app-feedback-inbox', standalone: true, imports: [CommonModule, FormsModule, TranslatePipe],
  templateUrl: './feedback-inbox.component.html',
})
export class FeedbackInboxComponent {
  private translation = inject(TranslationService);
  preset(status: 'processing' | 'declined') {
    this.replyStatus = status;
    this.replyBody = this.translation.translate(`feedback.${status}Reply`);
  }
  admin = input(false);
  /** False on the messages page, whose header already shows the title and description. */
  showHeading = input(true);
  readonly inbox = inject(FeedbackService);
  threads = computed(() => this.inbox.threads().filter(t => this.admin() || t.user_id === this.inbox.auth.currentUser()?.id));
  selectedId = signal<string | null>(null);
  confirmingDelete = signal(false);
  deleted = signal(false);
  selected = computed(() => this.threads().find(t => t.id === this.selectedId()));
  replies = signal<FeedbackReply[]>([]);
  kind = 'suggestion'; subject = ''; body = ''; replyBody = ''; replyStatus = 'processing';
  busy = signal(false); detailLoading = signal(false); failed = signal(false); sent = signal(false);
  statuses = ['new','processing','resolved','declined'];
  async open(thread: FeedbackThread) {
    if (this.busy()) return;
    this.confirmingDelete.set(false); this.deleted.set(false);
    this.selectedId.set(thread.id); this.replyBody = ''; this.replyStatus = thread.status === 'new' ? 'processing' : thread.status;
    this.replies.set([]); this.detailLoading.set(true); this.failed.set(false); this.sent.set(false);
    try {
      const replies = await this.inbox.replies(thread.id);
      if (this.selectedId() !== thread.id) return;
      this.replies.set(replies);
      await this.inbox.markRead(thread.id, this.admin());
    } catch { this.failed.set(true); }
    finally { if (this.selectedId() === thread.id) this.detailLoading.set(false); }
  }
  async send() {
    if (this.busy() || this.subject.trim().length < 3 || this.body.trim().length < 10) return;
    this.busy.set(true); this.failed.set(false); this.sent.set(false); this.deleted.set(false);
    try { await this.inbox.send(this.kind, this.subject, this.body); this.subject = ''; this.body = ''; this.sent.set(true); }
    catch { this.failed.set(true); } finally { this.busy.set(false); }
  }
  async respond() {
    const id = this.selectedId();
    if (!id || this.busy() || !this.replyBody.trim()) return;
    this.busy.set(true); this.failed.set(false); this.sent.set(false); this.deleted.set(false);
    try {
      await this.inbox.reply(id, this.replyBody, this.replyStatus);
      this.replies.set(await this.inbox.replies(id)); this.replyBody = ''; this.sent.set(true);
    } catch { this.failed.set(true); } finally { this.busy.set(false); }
  }
  async deleteThread() {
    const id = this.selectedId();
    if (!id || !this.admin() || !this.confirmingDelete() || this.busy() || this.detailLoading()) return;
    this.busy.set(true); this.failed.set(false); this.sent.set(false); this.deleted.set(false);
    try {
      await this.inbox.remove(id);
      this.selectedId.set(null); this.replies.set([]); this.replyBody = '';
      this.confirmingDelete.set(false); this.deleted.set(true);
    } catch { this.failed.set(true); }
    finally { this.busy.set(false); }
  }
}
